// ─── GRILLE DU MOIS (6 semaines × 7 jours) + légende ──────────────
// Extrait d'EcranCalendrier (découpage). Rond = garde (pastel), anneau =
// aujourd'hui / échange de garde, points = calendriers du jour, barres =
// vacances / férié / plages multi-jours continues. Swipe = navigation mois.
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { GestureDetector } from 'react-native-gesture-handler';
import { useUI, useT } from '@mop/core';
import { estMasqueEvenement } from './data/categories';
import { COL_EXC } from './services/garde';

const COL_VAC = '#B2DFDB';   // barre de vacances (teal)
const COL_FERIE = '#EF5350'; // barre de jour férié (rouge)

export default function GrilleMois({
  an, mo, sel, joursL, map, catMasquees, garde, gardeAff, gardeCol, gardeCalculee,
  montrerGarde, vacVisible, estVacances, estFerie, parents,
  semainesDuMois, ligneH, gridH, setGridH, vacPeriodes, feriesMap, swipeNav,
  numSemaine, dkDe, couleursJour, onTapJour, onLongJour, onTapSemaine,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const auj = new Date();

  return (
    <>
      <View style={s.semaine}>
        <View style={s.semNumTete} />
        {joursL.map((d, i) => <Text key={i} style={s.jourEntete}>{d}</Text>)}
      </View>

      <GestureDetector gesture={swipeNav}>
      {/* Bug Android : le `borderRadius` des pastilles n'est pas repeint quand
          la couleur de garde est AJOUTÉE/CHANGÉE sur une vue déjà montée. On
          force le REMONTAGE de la grille à chaque changement de mois ET à
          l'arrivée des données (garde / vacances / fériés). */}
      <View style={s.moisGrille}
        onLayout={(e) => { const h = e.nativeEvent.layout.height; if (h > 0 && Math.abs(h - gridH) > 1) setGridH(h); }}
        key={`${an}-${mo}-${Object.keys(gardeAff).length}-${vacPeriodes.length}-${feriesMap ? Object.keys(feriesMap).length : 0}`}>
        {semainesDuMois.map((sem, wi) => {
          const dPrem = sem[0] || null;   // lundi de la semaine (une vraie date)
          // Semaine 100 % hors mois → ligne INVISIBLE mais slot conservé (alignement).
          const semVide = !sem.some(d => d.getMonth() === mo);
          if (semVide) return (
            <View key={wi} style={[s.moisRow, { height: ligneH }]}>
              <View style={s.semNum} />
              {[0, 1, 2, 3, 4, 5, 6].map(i => <View key={i} style={s.cell2} />)}
            </View>
          );
          // Plages MULTI-JOURS traversant la semaine → barres CONTINUES : une
          // barre par plage, coins arrondis seulement aux VRAIES extrémités.
          const plagesSem = (() => {
            const par = new Map();
            sem.forEach((d, ci) => {
              const dk = dkDe(d);
              (map[dk] || []).forEach(ev => {
                if (!ev.estPlage || estMasqueEvenement(ev, catMasquees)) return;
                const id = ev.plageId || ev.id;
                const e = par.get(id) || { c0: ci, c1: ci, couleur: ev._couleur || UI.accent, debutReel: false, finReelle: false };
                e.c0 = Math.min(e.c0, ci); e.c1 = Math.max(e.c1, ci);
                if (ev.plageRole === 'debut' && ci === e.c0) e.debutReel = true;
                if (ev.plageRole === 'fin') e.finReelle = true;
                par.set(id, e);
              });
            });
            return [...par.values()].slice(0, 3);   // 3 barres max par semaine
          })();
          return (
            <View key={wi} style={[s.moisRow, { height: ligneH }]}>
              {/* Numéro de semaine — tap = vue Semaine (comme MoPCal). */}
              <TouchableOpacity style={s.semNum} disabled={!dPrem} onPress={() => onTapSemaine(dPrem, wi)} accessibilityLabel={t('calendrier.voirSemaineA11y')}>
                <Text style={s.semNumTxt}>{dPrem ? numSemaine(dPrem) : ''}</Text>
              </TouchableOpacity>
              {/* Barres de plages continues, empilées ; calque calé APRÈS la
                  colonne du n° de semaine → % justes. */}
              {plagesSem.length > 0 && (
                <View pointerEvents="none" style={s.barresPlages}>
                  {plagesSem.map((b, k) => (
                    <View key={`pl${k}`} style={[s.barrePlage, {
                      left: `${b.c0 * 100 / 7}%`, width: `${(b.c1 - b.c0 + 1) * 100 / 7}%`,
                      top: 2 + k * 5, backgroundColor: b.couleur,
                      borderTopLeftRadius: b.debutReel ? 2 : 0, borderBottomLeftRadius: b.debutReel ? 2 : 0,
                      borderTopRightRadius: b.finReelle ? 2 : 0, borderBottomRightRadius: b.finReelle ? 2 : 0,
                    }]} />
                  ))}
                </View>
              )}
              {sem.map((d, ci) => {
                const jn = d.getDate();
                const dk = dkDe(d);
                const estSel = jn === sel && d.getMonth() === mo && d.getFullYear() === an;
                const estAujd = jn === auj.getDate() && d.getMonth() === auj.getMonth() && d.getFullYear() === auj.getFullYear();
                const gd = gardeAff[dk];
                const gcol = gd ? (gardeCol[gd] || null) : null;
                // Exception de garde = surcharge stockée qui diffère du template :
                // remplissage = qui a réellement l'enfant, contour = qui l'aurait
                // eu sans l'échange (couleur du template).
                const gTpl = gardeCalculee(dk);
                const estExc = montrerGarde && !!garde[dk] && garde[dk] !== gTpl;
                const colExc = gardeCol[gTpl] || COL_EXC;
                const vac = vacVisible && estVacances(dk);
                const fer = estFerie(dk);
                const horsMois = d.getMonth() !== mo;
                return (
                  <TouchableOpacity key={ci}
                    style={[s.cell2, plagesSem.length > 0 && { paddingTop: 2 + plagesSem.length * 5 }]}
                    activeOpacity={0.6} onPress={() => onTapJour(d)}
                    onLongPress={() => onLongJour(d)} delayLongPress={350}>
                    {/* Bandeaux HORS du cadre de sélection. */}
                    <View style={[s.barreVac, { backgroundColor: fer ? COL_FERIE : vac ? COL_VAC : 'transparent' }]} />
                    {/* La SÉLECTION s'affiche sur le carré EXTERNE (cellule), pas le
                        rond : son style ne change JAMAIS → pas de repeint Android. */}
                    <View style={[s.cellule, horsMois && s.celluleHors, estSel && s.celluleSel]}>
                      <View style={[s.cercle, gcol && { backgroundColor: gcol }, estExc && !estAujd && [s.cercleExc, { borderColor: colExc }], estAujd && s.cercleAuj]}>
                        <Text style={[s.cercleTxt, gcol && s.cercleTxtInv]}>{jn}</Text>
                      </View>
                      <View style={s.indicateurs}>
                        {couleursJour(dk).map((c, k) => <View key={k} style={[s.point, { backgroundColor: c }]} />)}
                      </View>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          );
        })}
      </View>
      </GestureDetector>

      {/* Légende garde / vacances (les puces « parents » = co-parental). */}
      <View style={s.legende}>
        {montrerGarde && <View style={s.legItem}><View style={[s.legPuce, { backgroundColor: gardeCol.parent1 }]} /><Text style={s.legTxt}>{parents?.parent1?.nom || t('garde.parent1')}</Text></View>}
        {montrerGarde && <View style={s.legItem}><View style={[s.legPuce, { backgroundColor: gardeCol.parent2 }]} /><Text style={s.legTxt}>{parents?.parent2?.nom || t('garde.parent2')}</Text></View>}
        {vacVisible && <View style={s.legItem}><View style={[s.legTrait, { backgroundColor: COL_VAC }]} /><Text style={s.legTxt}>{t('calendrier.legendeVacances')}</Text></View>}
        <View style={s.legItem}><View style={[s.legTrait, { backgroundColor: COL_FERIE }]} /><Text style={s.legTxt}>{t('calendrier.legendeFerie')}</Text></View>
        {montrerGarde && <View style={s.legItem}><View style={s.legAnneau} /><Text style={s.legTxt}>{t('calendrier.legendeEchange')}</Text></View>}
      </View>
    </>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  semaine: { flexDirection: 'row', marginTop: 6, marginBottom: 4 },
  semNumTete: { width: 26 },
  jourEntete: { flex: 1, textAlign: 'center', fontSize: 12, fontWeight: '700', color: UI.faint },
  moisGrille: { flex: 1, marginTop: 2 },
  moisRow: { flexDirection: 'row', alignItems: 'stretch' },
  semNum: { width: 26, alignItems: 'center', justifyContent: 'center' },
  semNumTxt: { fontSize: 10.5, fontWeight: '700', color: UI.faint },
  cell2: { flex: 1, paddingVertical: 2 },
  barreVac: { height: 3, alignSelf: 'stretch', marginBottom: 6 },
  barresPlages: { position: 'absolute', left: 26, right: 0, top: 0, bottom: 0, zIndex: 2 },
  barrePlage: { position: 'absolute', height: 4 },
  cellule: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  celluleHors: { opacity: 0.45 },   // jours des mois adjacents : un peu plus pâles
  celluleSel: { borderWidth: 2, borderColor: UI.accent, borderRadius: 12, backgroundColor: UI.accentTint },
  cercle: { width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent', overflow: 'hidden' },
  cercleExc: { borderWidth: 2, borderColor: COL_EXC },   // anneau ambre = échange/exception
  cercleAuj: { borderWidth: 2.5, borderColor: UI.accent },
  cercleTxt: { fontSize: 13.5, fontWeight: '600', color: UI.ink },
  cercleTxtInv: { color: '#fff', fontWeight: '800' },
  indicateurs: { flexDirection: 'row', gap: 3, height: 8, marginTop: 2, alignItems: 'center' },
  point: { width: 5, height: 5, borderRadius: 3, backgroundColor: UI.accent },
  legende: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginTop: 10 },
  legItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  legPuce: { width: 11, height: 11, borderRadius: 6 },
  legTrait: { width: 16, height: 3, borderRadius: 2 },
  legAnneau: { width: 12, height: 12, borderRadius: 6, borderWidth: 2, borderColor: UI.muted, backgroundColor: 'transparent' },
  legTxt: { fontSize: 11, color: UI.muted, fontWeight: '600' },
});
